import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_LENGTH = 1;

export interface UseDebouncedSearchOptions<T> {
  searchFn: (term: string) => Promise<T[]>;
  minLength?: number;
  debounceMs?: number;
}

export interface UseDebouncedSearchReturn<T> {
  searchTerm: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  searchResults: T[];
  handleSearchTermChange: (event: ChangeEvent<HTMLInputElement>) => void;
  openSearch: () => void;
  closeDropdown: () => void;
}

export function useDebouncedSearch<T>({
  searchFn,
  minLength = DEFAULT_MIN_LENGTH,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseDebouncedSearchOptions<T>): UseDebouncedSearchReturn<T> {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<T[]>([]);

  const latestRequestIdRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingDebounce = useCallback(() => {
    if (!debounceTimerRef.current) return;
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = null;
  }, []);

  const closeDropdown = useCallback(() => {
    cancelPendingDebounce();
    setIsOpen(false);
    setSearchTerm('');
    setSearchResults([]);
    latestRequestIdRef.current += 1;
    setIsLoading(false);
  }, [cancelPendingDebounce]);

  useEffect(() => () => cancelPendingDebounce(), [cancelPendingDebounce]);

  const runSearch = useCallback(
    async (term: string) => {
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      setIsLoading(true);

      let results: T[] = [];
      try {
        results = await searchFn(term);
      } catch (error) {
        console.error('useDebouncedSearch: searchFn threw an error', error);
      }

      if (requestId !== latestRequestIdRef.current) return;
      setSearchResults(results);
      setIsLoading(false);
    },
    [searchFn],
  );

  const scheduleSearch = useCallback(
    (term: string) => {
      cancelPendingDebounce();
      debounceTimerRef.current = setTimeout(() => runSearch(term), debounceMs);
    },
    [cancelPendingDebounce, runSearch, debounceMs],
  );

  const handleSearchTermChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const term = event.target.value;
      setSearchTerm(term);
      setIsOpen(true);

      const trimmed = term.trim();
      if (trimmed.length < minLength) {
        cancelPendingDebounce();
        latestRequestIdRef.current += 1;
        setIsLoading(false);
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      scheduleSearch(trimmed);
    },
    [minLength, cancelPendingDebounce, scheduleSearch],
  );

  const openSearch = useCallback(() => {
    if (isOpen) return;
    setIsOpen(true);
    setIsLoading(true);
    scheduleSearch(searchTerm.trim());
  }, [isOpen, searchTerm, scheduleSearch]);

  return {
    searchTerm,
    isOpen,
    setIsOpen,
    isLoading,
    searchResults,
    handleSearchTermChange,
    openSearch,
    closeDropdown,
  };
}